import React from "react";
import PublicLayout from "../components/PublicLayout";
import { Target, Heart, Trophy, Users, Instagram, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";

// Inline X (formerly Twitter) glyph. lucide-react still ships the old bird
// `Twitter` icon — we want the post-2023 X mark to match brand guidelines.
// Sized via the `size` prop so it slots into the existing social-button row.
function XIcon({ size = 14, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      {...rest}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const stats = [
  { value: "10,000+", label: "Active aspirants" },
  { value: "1M+", label: "Study minutes logged" },
  { value: "4.8★", label: "Average rating" },
];

// Team — kept as a module-level constant so the JSX stays declarative.
// Each entry maps 1:1 to a TeamCard render below.
// `photo` is the editorial portrait; `photoFocus` is a Tailwind
// arbitrary-value `object-position` (e.g. "60%_40%") tuned per-image so the
// person's face stays centred in the circular crop.
const team = [
  {
    role: "Founder & CEO",
    badge: "Meet the Founder",
    name: "Praveen Kumar",
    initials: "PK",
    photo: "https://customer-assets.emergentagent.com/job_productivity-vault-11/artifacts/p430amqh_ebc456aa-a5d7-4baf-99dd-bb1a8ac1c0f4.jpeg",
    photoFocus: "object-[60%_35%]",
    // Emerald ring for the founder so they read as the primary brand voice
    ringClass: "ring-emerald-500/40 group-hover:ring-emerald-500/70",
    avatarFrom: "from-emerald-500",
    avatarTo: "to-emerald-700",
    paragraphs: [
      "Hi, I'm Praveen Kumar, Founder of Syllox.",
      "I come from a small village in Bihar, where opportunities were limited but dreams were not. While preparing for NEET, I personally experienced the struggles faced by millions of aspirants — inconsistency, distractions, burnout, and difficulty tracking progress.",
      "During my journey, I realized that most study apps focus only on lectures and content, but very few focus on productivity, discipline, and consistency.",
      "That realization became the foundation of Syllox.",
      "Syllox was built to help students stay focused, track their progress, analyze mock tests, solve doubts with AI, build better habits, and stay consistent every day.",
    ],
    mission: "To help aspirants become more disciplined, focused, and confident in achieving their dreams.",
    signoff: "— Praveen Kumar · Founder & CEO, Syllox",
    socials: [
      { type: "instagram", href: "https://www.instagram.com/praveenkr_03", label: "@praveenkr_03" },
      { type: "x",         href: "https://x.com/praveenkr_03",             label: "@praveenkr_03" },
      { type: "linkedin",  href: "https://www.linkedin.com/in/praveen-kumar-238791328", label: "Praveen Kumar" },
    ],
  },
  {
    role: "Co-Founder & Social Media Manager",
    badge: "Meet the Co-Founder",
    name: "Avni Singh",
    initials: "AS",
    photo: "https://customer-assets.emergentagent.com/job_productivity-vault-11/artifacts/g30nax5e_fb1eb9c6-14e1-45b7-a7b1-bb3d2c06e9bb.jpeg",
    photoFocus: "object-[55%_38%]",
    // Subtle emerald-teal ring to keep palette cohesive while staying distinct
    ringClass: "ring-teal-400/40 group-hover:ring-teal-400/70",
    avatarFrom: "from-amber-400",
    avatarTo: "to-rose-500",
    paragraphs: [
      "Avni Singh is the Co-Founder and Social Media Manager of Syllox.",
      "Growing up in a small village in Madhya Pradesh and later preparing for NEET in Kota, she experienced firsthand the challenges of managing studies, time, and consistency.",
      "These experiences inspired her to help build Syllox, a platform designed to make student life more organized, productive, and less stressful.",
      "At Syllox, she leads social media, community engagement, and brand growth while helping create meaningful solutions for aspirants across India.",
    ],
    mission: "Building a supportive student community where every aspirant feels connected, motivated, and never alone in their journey.",
    signoff: "— Avni Singh · Co-Founder & Social Media Manager, Syllox",
    socials: [
      { type: "instagram", href: "https://www.instagram.com/avnisingh.xo", label: "@avnisingh.xo" },
      { type: "linkedin",  href: "https://www.linkedin.com/in/avni-singh-0533973b7", label: "Avni Singh" },
    ],
  },
  {
    role: "Chief Operating Officer (COO)",
    badge: "Meet the COO",
    name: "Barnabas Upadhayaya",
    initials: "BU",
    photo: "https://customer-assets.emergentagent.com/job_productivity-vault-11/artifacts/b190a0we_c5db6319-688d-4ace-9daf-22622aa2066d.jpeg",
    // The portrait is a right-profile shot with the face centred slightly
    // to the upper-right. Nudge the object-position so the face stays
    // centred inside the circular crop (avoid cutting the forehead or chin).
    photoFocus: "object-[55%_30%]",
    // Cool steel-blue ring keeps the COO visually distinct from the emerald
    // (Founder) and teal (Co-Founder) treatments while staying inside the
    // same neutral-emerald palette family.
    ringClass: "ring-sky-400/40 group-hover:ring-sky-400/70",
    avatarFrom: "from-sky-500",
    avatarTo: "to-indigo-600",
    paragraphs: [
      "Hi, I'm Barnabas Upadhayaya, COO of Syllox.",
      "A former JEE aspirant, I understand the challenges students face during competitive exam preparation. Alongside my studies in Mathematics, I explore research in sustainability and scientific innovation, particularly microalgae-based systems.",
      "At Syllox, I lead operations and strategy with a simple mission: helping students stay focused, consistent, and confident throughout their preparation journey.",
      "I believe the right tools, guidance, and support can help students transform curiosity into achievement.",
    ],
    mission: "To create systems that help students study smarter, stay organized, and remain consistent throughout their preparation journey.",
    signoff: "— Barnabas Upadhayaya · COO, Syllox",
    socials: [
      { type: "instagram", href: "https://www.instagram.com/barna.bas08", label: "@barna.bas08" },
      { type: "linkedin",  href: "https://www.linkedin.com/in/barnabas-upadhayaya-b4819a2a3/", label: "Barnabas Upadhayaya" },
    ],
  },
  {
    role: "Chief Marketing Officer (CMO)",
    badge: "Meet the CMO",
    name: "Deepti Singh",
    initials: "DS",
    photo: "https://customer-assets-4nw71qhi.emergentagent.net/job_productivity-vault-11/artifacts/0r5jgb20_801D156C-7B13-4888-956D-E0855891A788.jpeg",
    // Portrait is centred; a small upward nudge keeps the face
    // well-framed inside the circular crop across viewports.
    photoFocus: "object-[50%_28%]",
    // Warm rose/pink ring keeps the CMO visually distinct from the
    // Founder (emerald), Co-Founder (teal), and COO (sky) treatments
    // while staying inside the same neutral palette family.
    ringClass: "ring-rose-400/40 group-hover:ring-rose-400/70",
    avatarFrom: "from-rose-500",
    avatarTo: "to-fuchsia-600",
    paragraphs: [
      "Hi, I'm Deepti Singh, Chief Marketing Officer at Syllox.",
      "As a NEET aspirant and aspiring entrepreneur, I understand the challenges students face while preparing for competitive exams. At Syllox, I lead marketing, community building, growth initiatives, and strategies that help us connect with and better serve students.",
      "Being part of Syllox gives me the opportunity to turn ideas into meaningful initiatives while learning entrepreneurship by building something from the ground up.",
      "My goal is to help Syllox reach more students and build a community that keeps them focused, consistent, and confident throughout their preparation journey.",
    ],
    mission: "Connecting more students with the right tools, ideas, and opportunities to make their preparation more focused and purposeful.",
    signoff: "— Deepti Singh · CMO, Syllox",
    socials: [
      { type: "linkedin", href: "https://www.linkedin.com/in/deepti-singh-730a96411/", label: "Deepti Singh" },
    ],
  },
];

const SOCIAL_ICONS = { instagram: Instagram, linkedin: Linkedin, x: XIcon };
const SOCIAL_ARIA = { instagram: "Instagram", linkedin: "LinkedIn", x: "X (formerly Twitter)" };

function TeamCard({ member }) {
  return (
    <article
      data-testid={`team-card-${member.initials.toLowerCase()}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-zinc-200/80 bg-white/80 p-7 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-500/5 dark:border-zinc-800/80 dark:bg-zinc-900/60 dark:hover:border-emerald-500/40 dark:hover:shadow-emerald-500/10 sm:p-8"
    >
      {/* Glassmorphism accent — soft emerald glow on hover */}
      <div className="pointer-events-none absolute inset-0 -z-10 rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:bg-emerald-500/[0.04]" />
      {/* Top-right brand corner light — adds depth on dark mode */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-emerald-500/10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100 dark:bg-emerald-400/20" />

      {/* Header — circular portrait + name + role */}
      <div className="flex items-start gap-5">
        <div className="relative shrink-0">
          {/* Soft gradient halo behind the photo for premium depth */}
          <div className={`absolute -inset-1 rounded-full bg-gradient-to-br ${member.avatarFrom} ${member.avatarTo} opacity-30 blur transition-opacity duration-500 group-hover:opacity-50`} />
          <div className={`relative h-24 w-24 overflow-hidden rounded-full bg-gradient-to-br ${member.avatarFrom} ${member.avatarTo} ring-4 ring-offset-2 ring-offset-white transition-all duration-500 dark:ring-offset-zinc-900 sm:h-28 sm:w-28 ${member.ringClass}`}>
            {member.photo ? (
              <img
                src={member.photo}
                alt={`${member.name} — ${member.role} at Syllox`}
                loading="lazy"
                decoding="async"
                className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 ${member.photoFocus || ""}`}
              />
            ) : (
              <div className="grid h-full w-full place-items-center font-heading text-2xl font-bold text-white">
                {member.initials}
              </div>
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1 pt-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">
            {member.badge}
          </div>
          <h3 className="mt-1 font-heading text-xl font-semibold leading-tight sm:text-2xl">
            {member.name}
          </h3>
          <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-400">{member.role}, Syllox</p>
        </div>
      </div>

      {/* Story */}
      <div className="mt-6 space-y-3 text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-300">
        {member.paragraphs.map((p, i) => (<p key={i}>{p}</p>))}
        {member.mission && (
          <blockquote className="mt-2 rounded-xl border-l-2 border-emerald-500 bg-emerald-50/60 px-4 py-3 text-[15px] italic text-emerald-900 dark:bg-emerald-500/5 dark:text-emerald-200">
            Our mission is simple: <span className="not-italic font-medium">{member.mission}</span>
          </blockquote>
        )}
      </div>

      {/* Signoff + socials pinned to bottom for visual balance between cards */}
      <div className="mt-auto pt-6">
        <p className="text-xs uppercase tracking-wider text-zinc-500">{member.signoff}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {member.socials.map((s) => {
            const Icon = SOCIAL_ICONS[s.type];
            return (
              <a
                key={s.type}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`team-${member.initials.toLowerCase()}-${s.type}`}
                className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 px-3.5 py-1.5 text-xs font-medium text-zinc-700 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-sm dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-300 dark:hover:border-emerald-500/40 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300"
                aria-label={`${member.name} on ${SOCIAL_ARIA[s.type] || s.type}`}
              >
                <Icon size={14} />
                <span>{s.label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </article>
  );
}

const values = [
  {
    icon: Target,
    title: "Built for the grind",
    body: "We obsess over the daily 2 AM grind. Every feature is shaped by what actually helps a 17-year-old crack NEET or JEE — nothing else.",
  },
  {
    icon: Heart,
    title: "Calm by design",
    body: "Most study apps shout. Syllox whispers. Clean dark mode, no streak-shaming, no fake gamification — just clarity.",
  },
  {
    icon: Trophy,
    title: "Outcomes over vanity",
    body: "We don't celebrate streaks for streaks' sake. Our reports surface real progress: chapters cleared, weak spots, hours that actually moved the needle.",
  },
  {
    icon: Users,
    title: "Aspirants, not customers",
    body: "Free tier is real. Premium is honest. We answer every support email personally because aspirants deserve a team that actually cares.",
  },
];

export default function About() {
  return (
    <PublicLayout
      title="About Us · Syllox — Built for Aspirants"
      description="Syllox is a study platform built by Indians for India's NEET and JEE aspirants. Track syllabus, focus with the timer, get AI doubt help, and study smarter."
    >
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8" data-testid="about-hero">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">About Syllox</div>
        <h1 className="mt-4 font-heading text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
          We build for the<br/>aspirants no one else<br/>builds for.
        </h1>
        <p className="mt-6 max-w-2xl text-base text-zinc-600 dark:text-zinc-400 sm:text-lg">
          Syllox is a productivity and study-tracking platform built specifically for India&apos;s NEET, JEE and competitive-exam aspirants. We help you visualise progress, focus deeper, and study smarter — without the noise of mainstream productivity tools.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-3 gap-4 rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900 sm:p-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-heading text-2xl font-bold text-emerald-600 sm:text-3xl">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-zinc-500 sm:text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl font-semibold sm:text-3xl">Our story</h2>
        <div className="mt-4 space-y-4 text-zinc-600 dark:text-zinc-400">
          <p>
            Syllox started as a personal frustration. The founder, a former NEET aspirant, was juggling 3 notebooks, 2 spreadsheets, a stopwatch app, and a calendar — just to keep track of which chapters were done and how many hours of physics were left this week.
          </p>
          <p>
            Existing study apps were either bloated productivity suites built for office workers or gamified toys that turned studying into a slot machine. Nothing was actually built for the specific rhythm of an Indian competitive-exam aspirant: long subjects, deep work blocks, weak-spot tracking, late-night doubts, peer accountability.
          </p>
          <p>
            So we built Syllox. One tool that handles syllabus tracking, a distraction-free stopwatch, weekly analytics, and an AI doubt-solver — all designed to fit how aspirants actually study.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8" data-testid="meet-the-team">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
          The people behind Syllox
        </div>
        <h2 className="mt-2 font-heading text-2xl font-semibold sm:text-3xl">
          Meet the team
        </h2>
        <p className="mt-3 max-w-2xl text-zinc-600 dark:text-zinc-400">
          Built by aspirants, for aspirants. The founding team has lived the late-night syllabus grind themselves — Syllox is the product they wished they had.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          {team.map((m) => (<TeamCard key={m.name} member={m} />))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl font-semibold sm:text-3xl">What we stand for</h2>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {values.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600">
                  <Icon size={20}/>
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold">{v.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{v.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-zinc-200 bg-gradient-to-br from-emerald-50 to-emerald-100/30 p-8 text-center dark:border-zinc-800 dark:from-emerald-500/10 dark:to-emerald-700/5 sm:p-12">
          {/* Official Syllox brand mark — same asset used across nav, footer,
              and every brand surface. Preserves the 48×48 rounded-2xl footprint
              of the original placeholder tile so the CTA card layout is
              unchanged. */}
          <Logo className="mx-auto h-12 w-12 !rounded-2xl" />
          <h3 className="mt-6 font-heading text-2xl font-semibold sm:text-3xl">Start your aspirant journey</h3>
          <p className="mx-auto mt-3 max-w-md text-zinc-600 dark:text-zinc-400">
            Join thousands of NEET/JEE aspirants who study smarter with Syllox. Free forever.
          </p>
          <Link to="/auth/phone" className="mt-6 inline-block">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="about-cta-signup">
              Create your account
            </Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
